import { CampaignStatus } from '../../../core/enum/campaign-status.enum';
import { InvestmentStatus } from '../../../core/enum/investment-status.enum';
import { RefundStatus } from '../../../core/enum/refund-status.enum';
import { InvestmentModel } from '../../../core/models/investment/investment.model';

// Derived investor-facing lifecycle bucket for an ECF investment.
// See PRD pitchin-issues-workflow-2#140 and CONTEXT.md glossary.
// Pure function — no DOM, no Angular. This is the high-value test seam.
export type EcfBucket = 'Active' | 'Allotted' | 'Closed';

const EXIT_STATUSES = [
  InvestmentStatus.Cancelled,
  InvestmentStatus.Refunded,
  InvestmentStatus.PledgeCancelled,
];

// Campaign stages where the campaign has already concluded one way or another —
// an investment sitting in one of these without a paid/completed payment is stale, not active.
const CONCLUDED_CAMPAIGN_STATUSES = [
  CampaignStatus.Success,
  CampaignStatus.ShareAllotment,
  CampaignStatus.Rejected,
];

// Payment states that mean the investor did pay — a concluded campaign doesn't make
// these stale, even though only Completed (fully allotted) counts as "Allotted" below.
const PAID_STATUSES = [InvestmentStatus.Paid, InvestmentStatus.Completed];

// First-match-wins. RefundRequested stays Active until fully Refunded.
// On a concluded campaign only a paid/completed investment is not stale; every other
// investment on a concluded campaign is treated as Closed.
export function classifyBucket(investment: InvestmentModel): EcfBucket {
  const campaignStatus = investment.campaign?.campaignStatus;
  const investmentStatus = investment.investmentStatus;

  if (
    EXIT_STATUSES.includes(investmentStatus) ||
    campaignStatus === CampaignStatus.Fail ||
    (CONCLUDED_CAMPAIGN_STATUSES.includes(campaignStatus) &&
      !PAID_STATUSES.includes(investmentStatus))
  ) {
    return 'Closed';
  }
  if (campaignStatus === CampaignStatus.Success && investmentStatus === InvestmentStatus.Completed) {
    return 'Allotted';
  }
  return 'Active';
}

// --- FRC-991: paid-but-unallotted ECF, for the dashboard donut's Cash segment ---

// Campaign stages where money has been paid in but shares have not been issued yet.
// Mirrors the backend's EcfHoldEligibility.EligibleCampaignStatuses (FRC-816) so the
// two definitions of "unallotted" cannot drift. Success/Fail are deliberately absent:
// that is exactly the point at which the money stops being cash and becomes either
// shares (Success) or a refund (Fail).
export const UNALLOTTED_CAMPAIGN_STATUSES = [
  CampaignStatus.PreLive,
  CampaignStatus.Live,
  CampaignStatus.Cooling,
  CampaignStatus.ShareAllotment,
];

// Payment states in which the investor's money is already sitting with pitchIN.
// PendingPayment and PaymentApprovalPending are excluded: nothing has been received
// and confirmed yet (a bank-in slip awaiting admin approval is not money in hand).
// RefundRequested IS included — the refund has been asked for but not paid out, so
// the cash is still held. Cancelled/Refunded/PledgeCancelled are money that has left.
const HELD_INVESTMENT_STATUSES = [
  InvestmentStatus.Paid,
  InvestmentStatus.Completed,
  InvestmentStatus.RefundRequested,
];

// Refund states where the money has actually gone back to the investor. Both
// "Requested" states are absent by design: RefundedAmount is populated the moment a
// refund is REQUESTED (see InvestmentService.HandleRefundInvestment /
// ShareService.FullRefundInvestment — "Status will be updated to Refunded when admin
// confirms refund"), so subtracting it on request would drop the investment out of
// Cash before the investor has been paid.
const PAID_OUT_REFUND_STATUSES = [RefundStatus.Refunded, RefundStatus.PartialRefunded];

/**
 * The portion of one investment that is still held as the investor's cash.
 *
 * `bankInAmount` (what was actually paid), less any refund that has genuinely been
 * paid out — which in practice only bites on a partial refund, since a full refund
 * moves the investment to a status that is not in HELD_INVESTMENT_STATUSES at all.
 * Clamped at 0 so an over-refund or a missing field can never subtract from Cash.
 *
 * Deliberately NOT falling back to `pledgedAmount` when this is 0. A row whose status
 * says Paid but whose bankInAmount is unset is inconsistent data — every payment path
 * sets BankInAmount = PledgedAmount on the transition into Paid — and reporting the
 * pledge as cash would paper over that instead of surfacing it. It shows up that way
 * on records inserted by hand with the status flipped directly, which is a test-data
 * artifact, not a state the payment flows can produce.
 */
function heldAmount(investment: InvestmentModel): number {
  const paid = investment.bankInAmount ?? 0;
  const refunded = PAID_OUT_REFUND_STATUSES.includes(investment.refundStatus)
    ? (investment.refundedAmount ?? 0)
    : 0;
  return Math.max(0, paid - refunded);
}

/**
 * True when this investment is paid-for money on a campaign that has not allotted yet.
 *
 * FRC-1007: this deliberately does NOT filter on paymentType. FRC-991 excluded
 * `PaymentType.Wallet` to stay disjoint from Wallet.pendingAmount, but every ECF
 * payment made through the current online flow is stamped Wallet: an FPX payment is
 * deposited into the wallet and then spent from it, so WalletService
 * .MakeInvestmentPaymentMain records PaymentType.Wallet even when the investor never
 * chose the wallet (see WalletProcessInvestmentSuccess.EcfInvestmentDepositAndReserveFunds).
 * The exclusion therefore dropped any ECF investment paid that way from the dashboard's
 * "ECF investment" figure, leaving only legacy bank-in/FPX-direct rows: on the reported
 * account it hid RM12 across three genuinely-paid wallet-stamped investments, which is
 * the whole of the RM20,001-vs-RM20,013 discrepancy.
 *
 * It is also what the ticket's stated rule asks for on its own terms: the ECF Investments
 * list does not filter on payment type, so neither can a figure that reconciles with it.
 *
 * Disjointness is now enforced on the wallet side instead: MyDashboardComponent
 * subtracts `wallet.ecfInvestmentHoldAmount` from the wallet term of Cash, so money
 * that is both held in the wallet and counted here is counted exactly once. Keep the
 * two in step — this filter and that subtraction are one fix, not two.
 */
export function isUnallottedEcf(investment: InvestmentModel): boolean {
  return (
    UNALLOTTED_CAMPAIGN_STATUSES.includes(investment?.campaign?.campaignStatus) &&
    HELD_INVESTMENT_STATUSES.includes(investment?.investmentStatus)
  );
}

/**
 * Total paid-but-unallotted ECF money for an investor — the FRC-991 addition to the
 * dashboard's Cash segment. Pure function, no Angular: this is the test seam.
 */
export function sumUnallottedEcf(investments: InvestmentModel[] | null | undefined): number {
  return (investments ?? [])
    .filter(isUnallottedEcf)
    .reduce((total, investment) => total + heldAmount(investment), 0);
}
